// Compact, lazy consult-recording player for the Transcript tab — deliberately NOT the full-page AudioPlayer (no positions API, no autoload).

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Progress, Slider } from "antd";
import { DownloadOutlined, PauseCircleOutlined, PlayCircleOutlined } from "@ant-design/icons";

import { useAudioStream } from "../../hooks/consult/useAudioStream";
import type { UserUuid } from "../../types/identity";

// A fresh nonce per click, so re-selecting the same segment still seeks.
export interface SeekTarget {
	seconds: number;
	nonce: number;
}

interface ConsultRecordingPlayerProps {
	patientId: UserUuid;
	fileId: string;
	appId: number;
	seekTo?: SeekTarget | null;
	onTimeUpdate?: (seconds: number) => void;
	onReady?: () => void;
}

export const RECORDING_LOAD_FAILED_MESSAGE = "The recording could not be loaded.";

const SKIP_SECONDS = 10;

const formatTime = (seconds: number): string => {
	const whole = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
	return `${Math.floor(whole / 60)}:${String(whole % 60).padStart(2, "0")}`;
};

const ConsultRecordingPlayer: React.FC<ConsultRecordingPlayerProps> = ({ patientId, fileId, appId, seekTo, onTimeUpdate, onReady }) => {
	const audioRef = useRef<HTMLAudioElement>(null);
	const urlRef = useRef<string | null>(null);
	const idsRef = useRef({ appId, fileId });
	const onReadyRef = useRef(onReady);
	const appliedSeekRef = useRef<number | null>(null);
	const [playing, setPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);

	idsRef.current = { appId, fileId };
	onReadyRef.current = onReady;

	// Stable identities: the hook's cleanup keys on `initialize`, and a new identity revokes the in-use URL.
	const handleLoadError = useCallback(() => {
		console.warn("consult.recording.load_failed", { app_id: idsRef.current.appId, file_id: idsRef.current.fileId });
	}, []);
	const handleLoadSuccess = useCallback(() => {}, []);

	const { audioUrl, loading, progress, error, initialize } = useAudioStream({ patientId, fileId, autoStart: false, source: "file", onError: handleLoadError, onSuccess: handleLoadSuccess });

	useEffect(() => {
		if (!audioUrl) return;
		urlRef.current = audioUrl;
		onReadyRef.current?.();
	}, [audioUrl]);

	// The hook's own cleanup captured a null URL on mount, so the revoke has to happen here.
	useEffect(
		() => () => {
			if (urlRef.current) URL.revokeObjectURL(urlRef.current);
		},
		[],
	);

	const seek = (seconds: number) => {
		const audio = audioRef.current;
		if (!audio) return;
		const target = Math.max(0, duration ? Math.min(seconds, duration) : seconds);
		audio.currentTime = target;
		setCurrentTime(target);
	};

	const play = () => {
		const audio = audioRef.current;
		if (!audio) return;
		setPlaying(true);
		audio.play()?.catch(() => setPlaying(false));
	};

	useEffect(() => {
		if (!seekTo || !audioUrl || appliedSeekRef.current === seekTo.nonce) return;
		appliedSeekRef.current = seekTo.nonce;
		seek(seekTo.seconds);
		play();
	}, [seekTo, audioUrl]);

	const togglePlay = () => {
		if (playing) {
			audioRef.current?.pause();
			setPlaying(false);
		} else {
			play();
		}
	};

	const handleTimeUpdate = () => {
		const seconds = audioRef.current?.currentTime ?? 0;
		setCurrentTime(seconds);
		onTimeUpdate?.(seconds);
	};

	const handleLoadedMetadata = () => {
		const value = audioRef.current?.duration ?? 0;
		setDuration(Number.isFinite(value) ? value : 0);
	};

	const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 12 };

	if (error && !audioUrl) {
		return (
			<Alert
				type="error"
				showIcon
				message={RECORDING_LOAD_FAILED_MESSAGE}
				data-testid="consult-recording-error"
				action={<Button size="small" onClick={() => initialize()} data-testid="consult-recording-retry">Retry</Button>}
				style={{ marginBottom: 12 }}
			/>
		);
	}

	if (!audioUrl) {
		return (
			<div style={rowStyle}>
				<Button icon={<PlayCircleOutlined />} loading={loading} onClick={() => initialize()} data-testid="consult-recording-load">
					Load recording
				</Button>
				{loading && <Progress percent={progress} size="small" data-testid="consult-recording-progress" style={{ flex: 1, minWidth: 120, marginBottom: 0 }} />}
				<Button icon={<DownloadOutlined />} disabled data-testid="consult-recording-download">
					Download
				</Button>
			</div>
		);
	}

	return (
		<div data-testid="consult-recording-player" style={{ ...rowStyle, padding: "8px 12px", borderRadius: 8, border: "1px solid var(--mg-border)", background: "var(--mg-bg-card)" }}>
			<audio ref={audioRef} src={audioUrl} preload="metadata" data-testid="consult-recording-audio" onTimeUpdate={handleTimeUpdate} onLoadedMetadata={handleLoadedMetadata} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)} />
			<Button type="text" icon={playing ? <PauseCircleOutlined /> : <PlayCircleOutlined />} onClick={togglePlay} aria-label={playing ? "Pause" : "Play"} data-testid="consult-recording-toggle" />
			<Button size="small" onClick={() => seek(currentTime - SKIP_SECONDS)} data-testid="consult-recording-back">
				-10s
			</Button>
			<Button size="small" onClick={() => seek(currentTime + SKIP_SECONDS)} data-testid="consult-recording-forward">
				+10s
			</Button>
			<Slider min={0} max={Math.max(duration, 1)} step={0.1} value={currentTime} onChange={seek} tooltip={{ formatter: (value) => formatTime(value ?? 0) }} style={{ flex: 1, minWidth: 120, margin: "0 8px" }} />
			<span data-testid="consult-recording-time" style={{ fontSize: 12, color: "var(--mg-text-secondary)", fontVariantNumeric: "tabular-nums" }}>
				{formatTime(currentTime)} / {formatTime(duration)}
			</span>
			<Button icon={<DownloadOutlined />} href={audioUrl} download={`consult-${appId}.mp3`} data-testid="consult-recording-download">
				Download
			</Button>
		</div>
	);
};

export default ConsultRecordingPlayer;
